import { useEffect } from 'react';
import './keyboardAwareSheets.less';

// Phones: on iOS (Safari and Chrome alike) the on-screen keyboard does not
// resize the page — it only shrinks the *visual* viewport and covers the
// bottom of the layout. Bottom sheets (modals / drawers docked to the bottom,
// e.g. the video player with its comments) stayed sized to the full screen,
// so the field being typed into sat under the keyboard.
//   • --m-kb / --m-vvh on <html>: the keyboard's height and the visible
//     height; html.m-keyboard-open while it is up — keyboardAwareSheets.less
//     lifts the sheets above it and caps them to what is visible;
//   • the focused field (in a sheet or on the page) is scrolled into the
//     visible middle once the keyboard has settled — only when it is
//     actually hidden.
// One install per app (phones only).

const FIELD = 'input, textarea, [contenteditable=""], [contenteditable="true"]';
// the keyboard is at least this tall — smaller gaps are browser toolbars
const KEYBOARD_MIN = 80;

const useKeyboardAwareSheets = (enabled: boolean) => {
  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return undefined;
    const root = document.documentElement;
    const viewport = window.visualViewport;
    let revealTimer = 0;

    const sync = () => {
      const visible = viewport ? viewport.height : window.innerHeight;
      const keyboard = viewport
        ? Math.max(0, window.innerHeight - viewport.height - viewport.offsetTop)
        : 0;
      root.style.setProperty('--m-vvh', `${Math.round(visible)}px`);
      root.style.setProperty('--m-kb', `${Math.round(keyboard)}px`);
      root.classList.toggle('m-keyboard-open', keyboard > KEYBOARD_MIN);
    };

    const reveal = () => {
      const field = document.activeElement as HTMLElement | null;
      if (!field?.matches?.(FIELD)) return;
      const rect = field.getBoundingClientRect();
      const top = viewport ? viewport.offsetTop : 0;
      const bottom = top + (viewport ? viewport.height : window.innerHeight);
      if (rect.top < top + 8 || rect.bottom > bottom - 8) {
        field.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
    };

    const scheduleReveal = (delay: number) => {
      window.clearTimeout(revealTimer);
      revealTimer = window.setTimeout(reveal, delay);
    };

    const onViewportChange = () => {
      sync();
      scheduleReveal(120);
    };
    // the keyboard animates in after focus (~300ms)
    const onFocusIn = () => scheduleReveal(350);

    sync();
    viewport?.addEventListener('resize', onViewportChange);
    viewport?.addEventListener('scroll', sync);
    window.addEventListener('resize', sync);
    document.addEventListener('focusin', onFocusIn, true);

    return () => {
      window.clearTimeout(revealTimer);
      viewport?.removeEventListener('resize', onViewportChange);
      viewport?.removeEventListener('scroll', sync);
      window.removeEventListener('resize', sync);
      document.removeEventListener('focusin', onFocusIn, true);
      root.style.removeProperty('--m-vvh');
      root.style.removeProperty('--m-kb');
      root.classList.remove('m-keyboard-open');
    };
  }, [enabled]);
};

export default useKeyboardAwareSheets;
